// Утилиты для извлечения значений из formContext/dataContext по путям. нужны, чтобы одинаково обрабатывать LocalPath/Path (в т.ч. "_.") и индексы коллекций [0]
// Используются в DynamicForm: `CollectionViewModel.ts` и `Models/index.ts`
export type Indexable = Record<string, unknown> | Array<unknown>;

// проверка, что значение можно индексировать как объект/массив
export const isIndexable = (value: unknown): value is Indexable => {
	return typeof value === 'object' && value !== null;
};

// достаем значение по пути с поддержкой [0]
export const getValueByPath = (root: unknown, path?: string): unknown => {
	if (!root || !path) {
		return undefined;
	}
	const normalized = path.startsWith('_.') ? path.slice(2) : path;
	const tokens: Array<string | number> = [];
	normalized.replace(/[^.[\]]+|\[(\d+)\]/g, (match, index) => {
		if (index !== undefined) {
			tokens.push(Number(index));
		} else {
			tokens.push(match);
		}
		return '';
	});
	let current: unknown = root;
	for (const token of tokens) {
		if (!isIndexable(current)) {
			return undefined;
		}
		if (typeof token === 'number') {
			current = Array.isArray(current)
				? current[token]
				: (current as Record<string, unknown>)[String(token)];
		} else {
			current = (current as Record<string, unknown>)[token];
		}
	}
	return current;
};

// ищем значение в контексте по LocalPath/Path и их упрощённым вариантам
export const resolveContextValueByPaths = (
	context: unknown,
	localPath?: string,
	fullPath?: string,
): unknown => {
	if (!isIndexable(context)) {
		return undefined;
	}
	const candidates: string[] = [];
	if (localPath && localPath !== '.') {
		candidates.push(localPath);
	}
	if (fullPath) {
		candidates.push(fullPath);
		if (fullPath.startsWith('_.')) {
			candidates.push(fullPath.slice(2));
		}
		const lastSegment = fullPath.includes('.')
			? fullPath.slice(fullPath.lastIndexOf('.') + 1)
			: fullPath;
		if (lastSegment && !candidates.includes(lastSegment)) {
			candidates.push(lastSegment);
		}
	}
	const contextRecord = context as Record<string, unknown>;
	for (const key of candidates) {
		if (key.includes('.') || key.includes('[')) {
			const byPath = getValueByPath(context, key);
			if (byPath !== undefined) {
				return byPath;
			}
		} else if (Object.prototype.hasOwnProperty.call(contextRecord, key)) {
			return contextRecord[key];
		}
	}
	if (fullPath) {
		const trimmed = fullPath.startsWith('_.')
			? fullPath.slice(2)
			: fullPath;
		const segments = trimmed.split('.').filter(Boolean);
		for (let start = 0; start < segments.length; start++) {
			const suffixPath = segments.slice(start).join('.');
			const byPath = getValueByPath(context, suffixPath);
			if (byPath !== undefined) {
				return byPath;
			}
		}
	}
	return undefined;
};

// парсим JSON, если значение пришло строкой
export const parseJsonIfString = (value: unknown): unknown => {
	if (typeof value !== 'string') {
		return value;
	}
	try {
		return JSON.parse(value);
	} catch {
		return value;
	}
};
